import {
    CalculatedPayout,
    calculateSportPayout,
    calculateSuperBonus,
    isSuperBonusEligible,
    MarketType,
    MonetaryInfo,
    OfferType,
    PayoutConfig,
    SelectionType,
    SuperBonusConfig,
    SuperBonusValidBetTypes,
    SuperOffer,
    TicketType,
} from '@superbet-group/betting.lib.payments';
import { AppConfig } from '@superbet-group/web.lib.config';
import { instanceGuard } from '@superbet-group/web.lib.utilities';
import { BetSlipPurchaseType } from '@superbet-group/web.types';

import { BetSlipData } from '@core/models/betSlip/IBetSlip';
import { Payout } from '@core/models/betSlip/Payout';
import SingletonError from '@core/services/common/errors/SingletonError';
import ISelection from '@models/betSlip/ISelection';
import { BetSlipType } from '@models/shared/betSlip/betSlipEnums';
import { getShellConfig } from '@src/config/shell/config';

function mapToClassicPayout(payout: CalculatedPayout): Payout {
    return {
        base: payout.maxWin.gross,
        bonus: payout.maxWin.bonus,
        tax: payout.maxWin.taxes.totalTax,
        final: payout.maxWin.net,
        minFinal: payout.minWin.net,
        taxPerTranche: payout.maxWin.taxes.brackets.map((bracket) => ({
            value: bracket.amount,
            percentage: bracket.rate,
        })),
        hasBeenLimited: payout.hasBeenLimited,
        combinationsOddSum: payout.combinationsOddSum,
    };
}

function getSuperOffer(tags: string, superOffers: SuperOffer[]): SuperOffer | null {
    for (let superOffer of superOffers) {
        if (tags.includes(superOffer)) {
            return superOffer;
        }
    }
    return null;
}

function getSelectedSystems(betSlip: BetSlipData): number[] {
    if (betSlip.type === BetSlipType.simple) {
        return [];
    }

    return betSlip.selectedSystems.map((system) => system.getMinNumber());
}

const purchaseTypeToTicketTypeMapper: Record<BetSlipPurchaseType, TicketType> = {
    [BetSlipPurchaseType.offline]: TicketType.retail,
    [BetSlipPurchaseType.online]: TicketType.online,
};

export class PayoutService {
    private static instance?: PayoutService;
    private readonly payoutConfig: Omit<PayoutConfig, 'handlingFeeRate'>;
    private readonly onlineTax: number;
    private readonly offlineTax: number;
    private readonly areSuperBonusSpecialsValidOnline: boolean;
    private readonly ignoredOfferTypes: SuperOffer[];
    public readonly isSuperBonusEnabled: boolean;
    public readonly superBonusMinCoefficient: number;
    public readonly superBonusNumberOfSelectionsMinimum: number;
    public isPriceBoostSbCalculationEnabled: boolean;

    public static getInstance(): PayoutService {
        return instanceGuard(PayoutService.instance);
    }

    public static createInstance(config: AppConfig): PayoutService {
        if (!PayoutService.instance) {
            PayoutService.instance = new PayoutService(config);
        }
        return PayoutService.instance;
    }

    private constructor(config: AppConfig) {
        if (PayoutService.instance) {
            throw new SingletonError(this.constructor.name);
        }

        this.isSuperBonusEnabled = config.betSlip.bonusConfig.superBonusEnabled;
        this.ignoredOfferTypes = config.betSlip.bonusConfig.ignoredOfferTypes;
        this.superBonusMinCoefficient = config.betSlip.bonusConfig.minOddValue;
        this.superBonusNumberOfSelectionsMinimum = config.superBonus.numberOfSelectionsMinimum;
        this.onlineTax = config.betSlip.onlineTax;
        this.offlineTax = config.betSlip.offlineTax;
        this.areSuperBonusSpecialsValidOnline = config.betSlip.areSuperBonusSpecialsValidOnline;
        this.payoutConfig = {
            handlingFeeInverse: config.betSlip.handlingFeeInverse,
            taxPerSystem: config.betSlip.taxPerSystem,
            maxWin: config.betSlip.maxWin,
            taxMode: config.betSlip.taxMode,
            taxBrackets: config.betSlip.taxBrackets,
            bonusConfig: config.betSlip.bonusConfig,
            taxBase: config.betSlip.taxBase,
            rounding: {
                amounts: 2,
                preFinal: {
                    totalTax: 3,
                    amounts: 3,
                },
                stakeCalculations: {
                    simple: 2,
                    system: null,
                },
                tax: {
                    brackets: 2,
                    total: 2,
                },
                roundToRealMoneyAmounts: true,
            },
        };
    }

    private getMarketType(selection: ISelection): MarketType {
        const ignoredOfferTypes = (this.payoutConfig.bonusConfig as SuperBonusConfig).ignoredOfferTypes;
        const superOfferType = getSuperOffer(selection.getTags(), ignoredOfferTypes);
        if (superOfferType) {
            return {
                kind: OfferType.SuperOffer,
                offerType: superOfferType,
            };
        }
        return {
            kind: OfferType.Base,
            selectionType: selection.isOngoing() ? SelectionType.live : SelectionType.prematch,
        };
    }

    private getPayoutConfig(betSlip: BetSlipData, monetaryInfo: MonetaryInfo): PayoutConfig {
        const { purchaseType } = betSlip;
        let bonusConfig = this.getBonusConfig(purchaseType);
        const superBonusEnabled = this.isBonusAvailableForSystemTicket(betSlip, monetaryInfo, bonusConfig);
        bonusConfig = bonusConfig !== null ? { ...bonusConfig, superBonusEnabled } : null;

        const perCombinations = purchaseType === BetSlipPurchaseType.offline && { perCombination: 8 };
        const stakeCalculations = {
            ...this.payoutConfig.rounding.stakeCalculations,
            ...perCombinations,
            system: purchaseType === BetSlipPurchaseType.online ? 2 : null,
        };
        const handlingFeeRate = purchaseType === BetSlipPurchaseType.online
            ? this.onlineTax
            : this.offlineTax;

        return {
            ...this.payoutConfig,
            rounding: {
                ...this.payoutConfig.rounding,
                stakeCalculations,
            },
            handlingFeeRate,
            bonusConfig,
        };
    }

    private getBonusConfig(purchaseType: BetSlipPurchaseType): SuperBonusConfig | null {
        const bonusConfig: SuperBonusConfig | null = this.payoutConfig.bonusConfig as (SuperBonusConfig | null);
        if (!bonusConfig) {
            return null;
        }
        const specialsAllowed = purchaseType === BetSlipPurchaseType.offline
            ? true
            : this.areSuperBonusSpecialsValidOnline;

        return {
            ...bonusConfig,
            isPriceBoostSbCalculationEnabled: this.isPriceBoostSbCalculationEnabled,
            specialsAllowed,
        };
    }

    private isBonusAvailableForSystemTicket(
        betSlip: BetSlipData,
        monetaryInfo: MonetaryInfo,
        bonusConfig: SuperBonusConfig | null,
    ): boolean {
        const { purchaseType, stake, selections, selectedSystems, type } = betSlip;
        if (!bonusConfig) {
            return false;
        }

        // When no systems are selected in the BetSlip, it behaves as a Simple bet from the `betting.lib.payments` perspective and SuperBonus value is present.
        // If SuperBonus is disabled for System bets, do not display the SuperBonus value in this default state.
        const isSystemBonusDisabledAndSelectionsEmpty = type === BetSlipType.system
            && selectedSystems.length === 0
            && !getShellConfig().remoteConfig.superBonus.validBetTypes.includes(SuperBonusValidBetTypes.System);

        if (isSystemBonusDisabledAndSelectionsEmpty) {
            return false;
        }

        const isSuperBonusUnlocked = this.getSuperBonusEligibility({
            stake: stake || 0,
            purchaseType,
            monetaryInfo,
            selections,
        }).isUnlocked;

        return purchaseType === BetSlipPurchaseType.offline
            ? isSuperBonusUnlocked
            : bonusConfig.superBonusEnabled;
    }

    public getPayout(betSlip: BetSlipData, monetaryInfo: MonetaryInfo): Payout {
        const systems = getSelectedSystems(betSlip);
        const selections = betSlip.selections.map((s) => ({
            odd: s.odd?.value || 0,
            fixed: s.isFixed,
            bonusable: s.odd?.isBonusable || false,
            marketType: this.getMarketType(s),
            tags: s.odd?.tags,
        }));

        const payout = calculateSportPayout(
            selections,
            systems,
            betSlip.stake,
            this.getPayoutConfig(betSlip, monetaryInfo),
        );

        return mapToClassicPayout(payout);
    }

    public setIsPriceBoostSbCalculationEnabled(value: boolean): void {
        this.isPriceBoostSbCalculationEnabled = value;
    }

    public getSuperBonus(selections: ISelection[], purchaseType: BetSlipPurchaseType) {
        const mappedSelections = this.mapToLibSelections(selections, purchaseType);

        return calculateSuperBonus(
            mappedSelections,
            this.getBonusConfig(purchaseType) as SuperBonusConfig,
        );
    }

    public getSuperBonusEligibility({ stake, purchaseType, monetaryInfo, selections }: {
        stake: number;
        purchaseType: BetSlipPurchaseType;
        monetaryInfo: MonetaryInfo;
        selections: ISelection[];
    }) {
        const mappedSelections = this.mapToLibSelections(selections, purchaseType);

        return isSuperBonusEligible(
            stake,
            purchaseTypeToTicketTypeMapper[purchaseType],
            monetaryInfo,
            mappedSelections,
            this.getBonusConfig(purchaseType) as SuperBonusConfig,
        );
    }

    private mapToLibSelections(
        selections: ISelection[],
        purchaseType: BetSlipPurchaseType,
    ) {
        const bonusConfig = this.getBonusConfig(purchaseType);
        const areSelectionsBelowMinOddTolerated = bonusConfig!.areSelectionsBelowMinOddTolerated;
        const minOddValue = bonusConfig!.minOddValue;

        return selections.map((s) => {
            const bonusable = s.odd?.isBonusable || false;
            const value = s.odd?.value || 0;
            return {
                odd: value,
                fixed: s.isFixed,
                bonusable: bonusable,
                tolerable: (s.odd?.isBonusable || false)
                && !!areSelectionsBelowMinOddTolerated
                && value < minOddValue,
                marketType: this.getMarketType(s),
                tags: s.odd?.tags,
            };
        });
    }

    public isOddValidForSuperBonus(tags: string | undefined): boolean {
        const ignoredOfferTypes = this.isPriceBoostSbCalculationEnabled
            ? this.ignoredOfferTypes
            : this.ignoredOfferTypes.filter((tag) => tag !== SuperOffer.priceBoost && tag !== SuperOffer.betBoost);
        return !tags || !getSuperOffer(tags, ignoredOfferTypes);
    }

    public static clearInstance(): void {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete PayoutService.instance;
    }
}
